import { z } from 'zod'
import { SchoolResponseSchema } from './school-application.schema'

export const ProviderApplicationStatusSchema = z.number().transform((val) => {
  switch (val) {
    case 1:
      return 'Invited' as const
    case 2:
      return 'Application In Progress' as const
    case 3:
      return 'Application Submitted' as const
    case 4:
      return 'Application Accepted' as const
    case 5:
      return 'Application Rejected' as const
    default:
      throw new Error(`Invalid application status code: ${val}`)
  }
})

export type ProviderApplicationStatus = z.infer<
  typeof ProviderApplicationStatusSchema
>

export const ProviderBankAccountStatusSchema = z
  .enum(['In Review', 'Accepted', 'Rejected'])
  .nullish()
  .transform((value) => value ?? null)

export type ProviderBankAccountStatus = z.infer<
  typeof ProviderBankAccountStatusSchema
>

const GlobalProviderCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
})

const ProviderCategorySchema = z.object({
  id: z.number(),
  global_provider_category: GlobalProviderCategorySchema,
})

const ProviderApplicationSchema = z
  .object({
    id: z.number(),
    provider_id: z.number().optional().nullable(),
    provider_categories: z.array(ProviderCategorySchema),
    global_status_id: z.number(),
    submitted_at: z.string().nullable().or(z.undefined()),
  })
  .transform((data) => ({
    ...data,
    status: ProviderApplicationStatusSchema.parse(data.global_status_id), // Convert `global_status_id` to `status`
  }))

export const ProviderStatus = {
  Active: 'Active',
  Inactive: 'Inactive',
  Pending: 'Pending',
} as const

export const ProviderStatusSchema = z.enum([
  ProviderStatus.Active,
  ProviderStatus.Inactive,
  ProviderStatus.Pending,
])

export type ProviderStatus = z.infer<typeof ProviderStatusSchema>

export const ProviderSchema = z
  .object({
    id: z.number(),
    provider_status: ProviderStatusSchema,
    name: z.string().nullable(),
    email: z.string(),
    location: z.string().nullable(),
    state_name: z.string().nullable(),
    created_datetime: z.string(),
    bank_account_status: ProviderBankAccountStatusSchema,
    provider_application: ProviderApplicationSchema,
  })
  .transform((data) => ({
    ...data,
    location: data.location ?? '',
  }))

export const ProviderDocumentSchema = z.object({
  id: z.number().nullable(),
  status: z.number().nullable(),
  global_provider_document_type: z.string().nullable(),
  comments: z.string().nullable(),
  path: z.string().nullable(),
  expiration_date: z.string().nullable(),
  trained: z.number().nullable(),
  provider_document_type_name: z.string().nullable(),
})

export type Provider = z.infer<typeof ProviderSchema>

const BooleanAsNumberSchema = z.union([z.literal(1), z.literal(0)])

const ProviderAffiliationSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  logo: z.string().nullable(),
})

export const ProviderDetailSchema = z
  .object({
    status: z.number(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    username: z.string().nullable(),
    name: z.string().nullable(),
    organization_description: z.string().nullable(),
    logo: z.string().nullable(),
    address: z.string().nullable(),
    address_line_2: z.string().nullable(),
    city: z.string().nullable(),
    global_state_id: z.number().nullable(),
    global_state_name: z.string().nullable(),
    zipcode: z.string().nullable(),
    customer_service_email: z.string().nullable(),
    customer_service_phone: z.string().nullable(),
    payments_support_email: z.string().nullable(),
    payments_support_phone: z.string().nullable(),
    phone_store: z.string().nullable(),
    mailing_address_different: z.number().nullable(),
    mailing_name: z.string().nullable(),
    mailing_email: z.string().nullable(),
    mailing_address: z.string().nullable(),
    mailing_address_line_2: z.string().nullable(),
    mailing_city: z.string().nullable(),
    mailing_global_state_id: z.number().nullable(),
    mailing_global_state_name: z.string().nullable(),
    mailing_zip_code: z.string().nullable(),
    allow_in_store_pickup: BooleanAsNumberSchema.nullable(),
    meta_data_tags: z.array(z.string()).nullable(),
    accepting_new_clients: BooleanAsNumberSchema.nullable(),
    header_background_color: z.string().nullable(),
    website: z.string().nullable(),
    marketplace: z.number().nullable(),
    surcharges: z.number().nullable(),
    surcharge_percentage: z.string().nullable(),
    status_name: z.string().nullable(),
    global_application_status_id: z.number().nullable(),
    global_business_category_id: z.number().nullable(),
    global_business_category_name: z.string().nullable(),
    global_business_type_id: z.number().nullable(),
    global_business_type_name: z.string().nullable(),
    global_business_filing_structure_id: z.number().nullable(),
    global_business_filing_structure_name: z.string().nullable(),
    applicant_first_name: z.string().nullable(),
    applicant_last_name: z.string().nullable(),
    applicant_phone_number: z.string().nullable(),
    fpc_first_name: z.string().nullable(),
    fpc_last_name: z.string().nullable(),
    fpc_email: z.string().nullable(),
    fpc_phone_number: z.string().nullable(),
    provider_category_other_description: z.string().nullable(),
    service_offered_other_description: z.string().nullable(),
    languages_supported_other_description: z.string().nullable(),
    multiple_locations: z.number().nullable(),
    service_locations: z.number().nullable(),
    provider_child_participating_ufa: z.number().nullable(),
    already_registered_on_classwallet: z.number().nullable(),
    comunication_consent: z.number().nullable(),
    attestation: z.number().nullable(),
    application_review: z.number().nullable(),
    send_me_copy_of_responses: z.number().nullable(),
    email_copy_of_responses: z.string().nullable(),
    school_application: SchoolResponseSchema.nullable(),
    delivery_methods: z.array(z.string()),
    provider_services: z.array(z.string()),
    languages: z.array(z.string()),
    provider_categories: z.array(z.string()),
    files: z.array(ProviderDocumentSchema),
    affiliations: z.array(ProviderAffiliationSummarySchema).default([]),
  })
  .transform((data) => ({
    ...data,
    application_status: ProviderApplicationStatusSchema.parse(
      data.global_application_status_id
    ), // Convert `global_status_id` to `status`
  }))

export type ProviderDetail = z.infer<typeof ProviderDetailSchema>

export const ProviderPreviewSettingsSchema = z.object({
  id: z.number(),
  provider_id: z.number(),
  header_background_color: z.string().nullable(),
  organization_description: z.string().nullable(),
  accepting_new_clients: z.union([z.literal(0), z.literal(1)]),
  meta_data_tags: z.array(z.any()).nullable(),
  allow_in_store_pickup: z.union([z.literal(0), z.literal(1)]),
  name: z.string(),
  legal_name: z.string().nullable(),
  website: z.string().nullable(),
  phone: z.string(),
  phone_store: z.string().nullable(),
  marketplace: z.union([z.literal(0), z.literal(1)]),
  address: z.string().nullable(),
  city: z.string().nullable(),
  global_state_id: z.number().nullable(),
  zipcode: z.string().nullable(),
  customer_service_email: z.string().nullable(),
  customer_service_phone: z.string().nullable(),
  logo: z.string().nullable(),
  mailing_address_different: z.number().nullable(),
  mailing_address: z.string().nullable(),
  mailing_city: z.string().nullable(),
  mailing_email: z.string().nullable(),
  mailing_global_state_id: z.number().nullable(),
  mailing_name: z.string().nullable(),
  mailing_zip_code: z.string().nullable(),
  payments_support_email: z.string().nullable(),
  payments_support_phone: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
})

export type ProviderPreviewSettings = z.infer<
  typeof ProviderPreviewSettingsSchema
>

export const AccessTokenPreviewResponseSchema = z.object({
  provider_id: z.string(),
  token: z.string(),
  expires_at: z.string(),
})

export type AccessTokenPreviewResponse = z.infer<
  typeof AccessTokenPreviewResponseSchema
>
